import { useCallback, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import MapBox, { type MapBoxHandle } from './components/MapBox';
import type { MapStatusFilterValue } from './components/MapStatusFilter';
import MapTrackingList from './components/MapTrackingList';
import { useMapTrackingPage } from './hooks/useMapTrackingPage';

function WardMapView() {
  const {
    filteredDevices,
    geofenceOverlays,
    isLoading,
    errorMessage,
    fetchInitialMapData,
    hoveredUserId,
    setHoveredUserId,
    selectedUserId,
    handleSelectUser,
    searchQuery,
    setSearchQuery,
    personStatuses,
  } = useMapTrackingPage();

  const [statusFilter, setStatusFilter] = useState<MapStatusFilterValue>('all');
  const mapBoxRef = useRef<MapBoxHandle>(null);

  const statusFilteredDevices = useMemo(() => {
    if (statusFilter === 'all') return filteredDevices;
    return filteredDevices.filter((device) => device.status === statusFilter);
  }, [filteredDevices, statusFilter]);

  const handleListSelectUser = useCallback(
    (id: number | null) => {
      if (id === null) {
        handleSelectUser(null);
        return;
      }

      const willSelect = selectedUserId !== id;
      if (!willSelect) {
        handleSelectUser(id);
        return;
      }

      const device = statusFilteredDevices.find((item) => item.person_id === id);
      if (
        device &&
        device.latitude !== null &&
        device.longitude !== null &&
        Number.isFinite(device.latitude) &&
        Number.isFinite(device.longitude)
      ) {
        mapBoxRef.current?.flyToUser(device.longitude as number, device.latitude as number);
      }

      handleSelectUser(id);
    },
    [handleSelectUser, selectedUserId, statusFilteredDevices]
  );

  return (
    <Box className='h-full min-h-[360px] overflow-hidden box-border px-[22px] pt-[22px] pb-[26px]'>
      <Box className='h-full overflow-hidden flex flex-row gap-5'>
        <Box className='flex-1 h-full min-w-0'>
          <MapBox
            ref={mapBoxRef}
            devices={statusFilteredDevices}
            devicesForStatusFilterCounts={filteredDevices}
            geofences={geofenceOverlays}
            isLoading={isLoading}
            errorMessage={errorMessage}
            onRetry={fetchInitialMapData}
            hoveredUserId={hoveredUserId}
            onHoveredUserChange={setHoveredUserId}
            selectedUserId={selectedUserId}
            onSelectUser={handleSelectUser}
            personStatuses={personStatuses}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </Box>
        <MapTrackingList
          devices={statusFilteredDevices}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          isLoading={isLoading}
          hoveredUserId={hoveredUserId}
          onHoveredUserChange={setHoveredUserId}
          selectedUserId={selectedUserId}
          onSelectUser={handleListSelectUser}
          personStatuses={personStatuses}
        />
      </Box>
    </Box>
  );
}

export default WardMapView;
